var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { IVector3 } from '../../api.js';
import { Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4, Matrix as BABYLON_Matrix } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
export const aabb_corners = [
    new BABYLON_Vector3(0, 0, 0),
    new BABYLON_Vector3(1, 0, 0),
    new BABYLON_Vector3(0, 1, 0),
    new BABYLON_Vector3(1, 1, 0),
    new BABYLON_Vector3(0, 0, 1),
    new BABYLON_Vector3(1, 0, 1),
    new BABYLON_Vector3(0, 1, 1),
    new BABYLON_Vector3(1, 1, 1)
];
export function is_aabb_nan(bounds) {
    return isNaN(bounds.min.x) ||
        isNaN(bounds.min.y) ||
        isNaN(bounds.min.z) ||
        isNaN(bounds.max.x) ||
        isNaN(bounds.max.y) ||
        isNaN(bounds.max.z);
}
export function is_aabb_valid(bounds) {
    // Here we consider the bounds for something with triangles with 0 area to still have valid bounds.
    // A proper test would need to verify that min < max in two of the three axes
    return (!is_aabb_nan(bounds)) &&
        bounds.min.x <= bounds.max.x &&
        bounds.min.y <= bounds.max.y &&
        bounds.min.z <= bounds.max.z;
}
export function update_aabb_by_transformed_aabb(in_out_bounds, object_bounds, object_transform) {
    const vertex = new BABYLON_Vector3(0, 0, 0);
    const transformed_vertex = new BABYLON_Vector4();
    const bounds_size = IVector3.subtract(object_bounds.max, object_bounds.min);
    for (let i = 0; i < aabb_corners.length; i++) {
        vertex.x = object_bounds.min.x + aabb_corners[i].x * bounds_size.x;
        vertex.y = object_bounds.min.y + aabb_corners[i].y * bounds_size.y;
        vertex.z = object_bounds.min.z + aabb_corners[i].z * bounds_size.z;
        BABYLON_Vector4.TransformCoordinatesToRef(vertex, object_transform, transformed_vertex);
        IVector3.min_to_ref(in_out_bounds.min, transformed_vertex, in_out_bounds.min);
        IVector3.max_to_ref(in_out_bounds.max, transformed_vertex, in_out_bounds.max);
    }
}
function get_object_bounds(root, root_transform, include_children) {
    return __awaiter(this, void 0, void 0, function* () {
        const transform_bounds_tuples = [];
        transform_bounds_tuples.push({
            object: root,
            bounds: root.get_local_bounds(),
            transform: root_transform
        });
        if (include_children) {
            for (let i = 0; i < transform_bounds_tuples.length; i++) {
                const parent = transform_bounds_tuples[i].object;
                parent.children_direct_descendants_iterator((child) => {
                    transform_bounds_tuples.push({
                        object: child,
                        bounds: child.get_local_bounds(),
                        transform: child.get_local_matrix().multiply(transform_bounds_tuples[i].transform)
                    });
                });
            }
        }
        const bounds = yield Promise.all(transform_bounds_tuples.map(value => value.bounds));
        const total_bounds = {
            min: new BABYLON_Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE),
            max: new BABYLON_Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE)
        };
        for (let i = 0; i < transform_bounds_tuples.length; i++) {
            if (is_aabb_valid(bounds[i].aabb)) {
                update_aabb_by_transformed_aabb(total_bounds, bounds[i].aabb, transform_bounds_tuples[i].transform);
            }
        }
        return total_bounds;
    });
}
export function get_object_bounds_world(objects) {
    return __awaiter(this, void 0, void 0, function* () {
        const promises = objects.map((object) => {
            return get_object_bounds(object, object.get_world_matrix(), true);
        });
        const bounds = yield Promise.all(promises);
        const total_bounds = {
            min: new BABYLON_Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE),
            max: new BABYLON_Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE)
        };
        for (let i = 0; i < bounds.length; i++) {
            if (is_aabb_valid(bounds[i])) {
                IVector3.min_to_ref(total_bounds.min, bounds[i].min, total_bounds.min);
                IVector3.max_to_ref(total_bounds.max, bounds[i].max, total_bounds.max);
            }
        }
        return validate_bounds(total_bounds);
    });
}
export function get_object_bounds_local(object, include_children) {
    return __awaiter(this, void 0, void 0, function* () {
        const bounds = yield get_object_bounds(object, BABYLON_Matrix.Identity(), include_children);
        return validate_bounds(bounds);
    });
}
function validate_bounds(bounds) {
    if (is_aabb_valid(bounds)) {
        return bounds;
    }
    else if (is_aabb_nan(bounds)) {
        throw new Error('Calculated bounds contain NaN');
    }
    else {
        // This case is when we have nothing with bounds in the scene
        // This is most likely because nothing has loaded yet
        // We then consider the the world bounds to be all zeros
        return { min: BABYLON_Vector3.Zero(), max: BABYLON_Vector3.Zero() };
    }
}
/**
 * calculate center of aabb
 * @param aabb to check
 * @returns center of aabb
 */
export function aabb_center(aabb) {
    return IVector3.scale(IVector3.add(aabb.min, aabb.max), 0.5);
}
/**
 * calculate size of aabb
 * @param aabb to check
 * @returns size of aabb
 */
export function aabb_size(aabb) {
    return IVector3.subtract(aabb.max, aabb.min);
}
/**
 * Type narrow IBounds to IBoundingBox
 * @param bounds to check
 * @returns true if IBounds is IBoundingBox, false otherwise
 */
export function is_aabb(bounds) {
    const min = 'min';
    const max = 'max';
    return min in bounds && max in bounds;
}
/**
 * Type narrow IBounds to IboundingSphere
 * @param bounds to check
 * @returns true if IBounds is IBoundingSphere, false otherwise
 */
export function is_sphere(bounds) {
    const position = 'position';
    const radius = 'radius';
    return position in bounds && radius in bounds;
}
export function clone_aabb(aabb) {
    const clone = {
        min: { x: aabb.min.x, y: aabb.min.y, z: aabb.min.z },
        max: { x: aabb.max.x, y: aabb.max.y, z: aabb.max.z }
    };
    return clone;
}
export function clone_sphere(sphere) {
    const clone = {
        position: { x: sphere.position.x, y: sphere.position.y, z: sphere.position.z },
        radius: sphere.radius
    };
    return clone;
}
export function clone_bounds(bounds) {
    if (is_aabb(bounds)) {
        return clone_aabb(bounds);
    }
    else if (is_sphere(bounds)) {
        return clone_sphere(bounds);
    }
    throw Error('Tried to clone bounds of unknown sub-type');
}
export function is_valid_bounding_box(bounding_box) {
    if (bounding_box.maximum.x < bounding_box.minimum.x || bounding_box.maximum.y < bounding_box.minimum.y || bounding_box.maximum.z < bounding_box.minimum.z)
        return false;
    if (bounding_box.minimum.x === bounding_box.maximum.x && bounding_box.minimum.y === bounding_box.maximum.y && bounding_box.minimum.z === bounding_box.maximum.z)
        return false;
    return true;
}
//# sourceMappingURL=Bounds.js.map